import React, { useState, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiUsers,
  FiUserPlus,
  FiUserMinus,
  FiClock,
  FiCheck,
  FiX,
  FiMessageCircle,
  FiLoader,
  FiStar,
} from "react-icons/fi";
import { useRoom } from "../../../context/RoomContext";
import { UserContext } from "../../../context/UserContext";
import { formatTimeAgo } from "../../../utils/roomHelpers";
import "./MembersSidebar.css";

// ==============================================
// Helper Functions
// ==============================================

// Generate initials from name
const getInitials = (name) => {
  if (!name || typeof name !== "string") return "?";
  const parts = name.trim().split(" ").filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

// Get avatar color based on name
const getAvatarColor = (name) => {
  const colors = [
    { bg: "#dbeafe", text: "#3b82f6" }, // Blue
    { bg: "#d1fae5", text: "#10b981" }, // Green
    { bg: "#fef3c7", text: "#f59e0b" }, // Amber
    { bg: "#e0e7ff", text: "#6366f1" }, // Indigo
    { bg: "#cffafe", text: "#06b6d4" }, // Cyan
    { bg: "#fce7f3", text: "#ec4899" }, // Pink
  ];
  const index = name ? name.charCodeAt(0) % colors.length : 0;
  return colors[index];
};

// ==============================================
// Sub-Components
// ==============================================

// Avatar Component
const Avatar = ({ src, name, size = "md", isLeader = false }) => {
  const [imageError, setImageError] = useState(false);
  const hasValidImage = src && !imageError;
  const initials = getInitials(name);
  const colors = getAvatarColor(name || "");

  const sizeClasses = {
    sm: "msb-avatar--sm",
    md: "msb-avatar--md",
    lg: "msb-avatar--lg",
  };

  if (hasValidImage) {
    return (
      <div className={`msb-avatar-wrapper ${isLeader ? "msb-avatar-wrapper--leader" : ""}`}>
        <img
          src={src}
          alt={name || "User"}
          className={`msb-avatar ${sizeClasses[size]}`}
          onError={() => setImageError(true)}
        />
        {isLeader && (
          <div className="msb-leader-crown">
            <FiStar />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`msb-avatar-wrapper ${isLeader ? "msb-avatar-wrapper--leader" : ""}`}>
      <div
        className={`msb-avatar-initials ${sizeClasses[size]}`}
        style={{ backgroundColor: colors.bg, color: colors.text }}
        title={name || "Unknown User"}
      >
        {initials}
      </div>
      {isLeader && (
        <div className="msb-leader-crown">
          <FiStar />
        </div>
      )}
    </div>
  );
};

// Section Header
const SectionHeader = ({ icon: Icon, title, count, maxCount, color = "blue" }) => (
  <div className="msb-section-header" data-color={color}>
    <div className="msb-section-icon">
      <Icon />
    </div>
    <h3 className="msb-section-title">{title}</h3>
    {count !== undefined && (
      <span className="msb-section-count">
        {count}{maxCount ? `/${maxCount}` : ""}
      </span>
    )}
  </div>
);

// Member Card
const MemberCard = ({
  user,
  isLeader = false,
  isCurrentUser = false,
  joinedAt,
  canRemove = false,
  onRemove,
  loading = false,
}) => {
  const userName = user?.name || "Unknown";

  return (
    <motion.div
      className={`msb-member-card ${isLeader ? "msb-member-card--leader" : ""}`}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
    >
      <Avatar
        src={user?.profilePhotoUrl}
        name={userName}
        size="md"
        isLeader={isLeader}
      />
      <div className="msb-member-info">
        <div className="msb-member-name">
          <span>{userName}</span>
          {isCurrentUser && <span className="msb-you-badge">You</span>}
        </div>
        {isLeader ? (
          <div className="msb-member-role">Lab Leader</div>
        ) : joinedAt ? (
          <div className="msb-member-meta">
            <FiClock />
            <span>Joined {formatTimeAgo(joinedAt)}</span>
          </div>
        ) : null}
      </div>
      {canRemove && (
        <motion.button
          className="msb-btn-remove"
          onClick={() => onRemove(user?._id || user?.id)}
          disabled={loading}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          title="Remove member"
        >
          <FiUserMinus />
        </motion.button>
      )}
    </motion.div>
  );
};

// Join Request Card
const JoinRequestCard = ({
  request,
  onAccept,
  onReject,
  loading = false,
}) => {
  const user = request.user || {};
  const userId = user._id || user.id;
  const userName = user.name || "Unknown";

  return (
    <motion.div
      className="msb-request-card"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
    >
      <div className="msb-request-header">
        <Avatar src={user.profilePhotoUrl} name={userName} size="md" />
        <div className="msb-request-info">
          <div className="msb-request-name">{userName}</div>
          {user.field && <div className="msb-request-field">{user.field}</div>}
          <div className="msb-request-time">
            <FiClock />
            <span>{formatTimeAgo(request.requestedAt || request.createdAt)}</span>
          </div>
        </div>
      </div>

      {request.message && (
        <div className="msb-request-message">
          <FiMessageCircle className="msb-message-icon" />
          <p className="msb-message-text">"{request.message}"</p>
        </div>
      )}

      <div className="msb-request-actions">
        <motion.button
          className="msb-btn-reject"
          onClick={() => onReject(userId)}
          disabled={loading}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <FiX />
          <span>Reject</span>
        </motion.button>
        <motion.button
          className="msb-btn-accept"
          onClick={() => onAccept(userId)}
          disabled={loading}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          {loading ? (
            <motion.span
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              style={{ display: "inline-flex" }}
            >
              <FiLoader />
            </motion.span>
          ) : (
            <FiCheck />
          )}
          <span>Accept</span>
        </motion.button>
      </div>
    </motion.div>
  );
};

// ==============================================
// Main Component
// ==============================================
const MembersSidebar = ({ room, userRole }) => {
  const { user } = useContext(UserContext);
  const { handleJoinRequest, removeMember, loading } = useRoom();

  const userId = user?.id || user?._id;
  const isLeader = userRole?.isLeader || false;
  const memberCount = room?.members?.length || 0;
  const maxMembers = room?.maxMembers || 8;

  // Handlers
  const handleAcceptRequest = async (requestUserId) => {
    try {
      await handleJoinRequest(room._id, requestUserId, "accept");
    } catch (err) { /* ignored */ }
  };

  const handleRejectRequest = async (requestUserId) => {
    try {
      await handleJoinRequest(room._id, requestUserId, "reject");
    } catch (err) { /* ignored */ }
  };

  const handleRemoveMember = async (memberId) => {
    if (window.confirm("Are you sure you want to remove this member?")) {
      try {
        await removeMember(room._id, memberId);
      } catch (err) { /* ignored */ }
    }
  };

  // Filter members (exclude leader from members list)
  const leaderId = room.leader?._id || room.leader?.id;
  const otherMembers = room.members?.filter((member) => {
    const mId = member.user?._id || member.user?.id;
    return mId !== leaderId;
  }) || [];

  return (
    <div className="msb-container">
      {/* Leader - No header needed, role shown in card */}
      <div className="msb-section">
        <MemberCard
          user={room.leader}
          isLeader={true}
          isCurrentUser={leaderId === userId}
        />
      </div>

      {/* Members Section */}
      <div className="msb-section">
        <SectionHeader
          icon={FiUsers}
          title="Members"
          count={memberCount}
          maxCount={maxMembers}
          color="blue"
        />
        <div className="msb-members-list">
          <AnimatePresence>
            {otherMembers.length > 0 ? (
              otherMembers.map((member) => {
                const mId = member.user?._id || member.user?.id;
                const isCurrentUser = mId === userId;

                return (
                  <MemberCard
                    key={mId}
                    user={member.user}
                    isCurrentUser={isCurrentUser}
                    joinedAt={member.joinedAt}
                    canRemove={isLeader && !isCurrentUser}
                    onRemove={handleRemoveMember}
                    loading={loading}
                  />
                );
              })
            ) : (
              <div className="msb-empty">
                <FiUsers />
                <p>No other members yet</p>
              </div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Join Requests Section (Leader Only) */}
      {isLeader && room.joinRequests && room.joinRequests.length > 0 && (
        <div className="msb-section">
          <SectionHeader
            icon={FiUserPlus}
            title="Join Requests"
            count={room.joinRequests.length}
            color="cyan"
          />
          <div className="msb-requests-list">
            <AnimatePresence>
              {room.joinRequests.map((request) => {
                const reqId = request.user?._id || request.user?.id;
                return (
                  <JoinRequestCard
                    key={reqId}
                    request={request}
                    onAccept={handleAcceptRequest}
                    onReject={handleRejectRequest}
                    loading={loading}
                  />
                );
              })}
            </AnimatePresence>
          </div>
        </div>
      )}
    </div>
  );
};

export default MembersSidebar;
